import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import stateCityData from '../data/state_city.json';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import TransactionDocumentUploadModal from '../components/transaction/TransactionDocumentUploadModal';
import TransactionReviewModal from '../components/transaction/TransactionReviewModal';
import { track } from '../analytics/analytics';
import { EVENTS } from '../analytics/events';
import { parseTransactionDocuments } from '../services/ria.service';
import { tcService } from '../services/tc.service';
import './PurchaseTransactionPage.css';

// ZIP Code validation ranges by state (all 50 US states + DC)
const ZIP_CODE_RANGES = {
  'Alabama': { ranges: [[35004, 36925]], prefix: '35-36' },
  'Alaska': { ranges: [[99501, 99950]], prefix: '995-999' },
  'Arizona': { ranges: [[85001, 86556]], prefix: '85-86' },
  'Arkansas': { ranges: [[71601, 72959], [75502, 75502]], prefix: '71-72, 755' },
  'California': { ranges: [[90001, 96162]], prefix: '90-96' },
  'Colorado': { ranges: [[80001, 81658]], prefix: '80-81' },
  'Connecticut': { ranges: [[6001, 6928]], prefix: '06' },
  'Delaware': { ranges: [[19701, 19980]], prefix: '197-199' },
  'District of Columbia': { ranges: [[20001, 20599], [56901, 56999]], prefix: '200-205, 569' },
  'Florida': { ranges: [[32003, 34997]], prefix: '32-34' },
  'Georgia': { ranges: [[30002, 31999], [39813, 39901]], prefix: '30-31, 398-399' },
  'Hawaii': { ranges: [[96701, 96898]], prefix: '967-968' },
  'Idaho': { ranges: [[83201, 83877]], prefix: '832-838' },
  'Illinois': { ranges: [[60001, 62999]], prefix: '60-62' },
  'Indiana': { ranges: [[46001, 47997]], prefix: '46-47' },
  'Iowa': { ranges: [[50001, 52809]], prefix: '50-52' },
  'Kansas': { ranges: [[66002, 67954]], prefix: '66-67' },
  'Kentucky': { ranges: [[40003, 42788]], prefix: '40-42' },
  'Louisiana': { ranges: [[70001, 71497]], prefix: '70-71' },
  'Maine': { ranges: [[3901, 4992]], prefix: '039-049' },
  'Maryland': { ranges: [[20588, 21930]], prefix: '206-219' },
  'Massachusetts': { ranges: [[1001, 2791], [5501, 5544]], prefix: '010-027, 055' },
  'Michigan': { ranges: [[48001, 49971]], prefix: '48-49' },
  'Minnesota': { ranges: [[55001, 56763]], prefix: '55-56' },
  'Mississippi': { ranges: [[38601, 39776]], prefix: '386-397' },
  'Missouri': { ranges: [[63001, 65899]], prefix: '63-65' },
  'Montana': { ranges: [[59001, 59937]], prefix: '59' },
  'Nebraska': { ranges: [[68001, 69367]], prefix: '68-69' },
  'Nevada': { ranges: [[88901, 89883]], prefix: '889-898' },
  'New Hampshire': { ranges: [[3031, 3897]], prefix: '030-038' },
  'New Jersey': { ranges: [[7001, 8989]], prefix: '07-08' },
  'New Mexico': { ranges: [[87001, 88439]], prefix: '87-88' },
  'New York': { ranges: [[10001, 14925], [6390, 6390]], prefix: '100-149, 063' },
  'North Carolina': { ranges: [[27006, 28909]], prefix: '27-28' },
  'North Dakota': { ranges: [[58001, 58856]], prefix: '58' },
  'Ohio': { ranges: [[43001, 45999]], prefix: '43-45' },
  'Oklahoma': { ranges: [[73001, 74966]], prefix: '73-74' },
  'Oregon': { ranges: [[97001, 97920]], prefix: '97' },
  'Pennsylvania': { ranges: [[15001, 19640]], prefix: '150-196' },
  'Rhode Island': { ranges: [[2801, 2940]], prefix: '028-029' },
  'South Carolina': { ranges: [[29001, 29945]], prefix: '29' },
  'South Dakota': { ranges: [[57001, 57799]], prefix: '57' },
  'Tennessee': { ranges: [[37010, 38589]], prefix: '370-385' },
  'Texas': { ranges: [[73301, 73301], [75001, 79999], [88510, 88589]], prefix: '733, 75-79, 885' },
  'Utah': { ranges: [[84001, 84791]], prefix: '84' },
  'Vermont': { ranges: [[5001, 5907]], prefix: '05' },
  'Virginia': { ranges: [[20101, 24658]], prefix: '201-246' },
  'Washington': { ranges: [[98001, 99403]], prefix: '980-994' },
  'West Virginia': { ranges: [[24701, 26886]], prefix: '247-268' },
  'Wisconsin': { ranges: [[53001, 54990]], prefix: '53-54' },
  'Wyoming': { ranges: [[82001, 83128]], prefix: '82-83' }
};

const validateZipCode = (zipCode, stateName) => {
  if (!zipCode || !stateName) return { isValid: false, message: '' };
  const cleanZip = zipCode.replace(/[\s-]/g, '');
  if (!/^\d{5}(-?\d{4})?$/.test(cleanZip)) {
    return { isValid: false, message: 'ZIP code must be 5 digits (e.g., 12345) or 9 digits (e.g., 12345-6789)' };
  }
  const zip5 = parseInt(cleanZip.substring(0, 5), 10);
  const stateRules = ZIP_CODE_RANGES[stateName];
  if (!stateRules) return { isValid: true, message: '' };
  const isInRange = stateRules.ranges.some(([min, max]) => zip5 >= min && zip5 <= max);
  if (!isInRange) {
    return { isValid: false, message: `Invalid ZIP code for ${stateName}. Valid ZIP codes start with: ${stateRules.prefix}` };
  }
  return { isValid: true, message: '' };
};

const getZipCodeHint = (stateName) => {
  const stateRules = ZIP_CODE_RANGES[stateName];
  return stateRules ? `${stateName} ZIP codes start with: ${stateRules.prefix}` : '';
};

const getStateNameFromCode = (stateCode) => {
  const stateData = stateCityData.find((item) => item.stateCode === stateCode);
  return stateData ? stateData.state : stateCode;
};

// Map UI status labels to API status values
const STATUS_OPTIONS = [
  { label: 'Prospect', value: 'PENDING' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Fell through', value: 'CANCELLED' },
  { label: 'Closed', value: 'CLOSED' },
];

// Financing types supported by the API
const FINANCING_TYPES = [
  { label: 'Conventional', value: 'CONVENTIONAL' },
  { label: 'Cash', value: 'CASH' },
  { label: 'FHA', value: 'FHA' },
  { label: 'VA', value: 'VA' },
];

// Side-aware ACTIVE role classification — mirrors mobile CreateTransactionModal.tsx
// and TransactionDetailPage.js's prospect->active conversion flow exactly: the AI
// parser's own `classification` field is unreliable (LLM drift can mark the buyer
// or buyer's agent as REFERENCE), so ACTIVE is derived purely from `role` instead
// of trusted from the parse response. Trusting `p.classification` here silently
// dropped the buyer's email invite whenever the parser mis-tagged them —
// this page is BUYING-only, so only the buying-side role set applies.
const BUYING_ACTIVE_ROLES = new Set([
  'BUYER', 'CO_BUYER', 'BUYER_AGENT', 'BUYERS_AGENT', 'CO_BUYERS_AGENT',
  'BUYING_BROKERAGE', 'BUYER_BROKERAGE', 'BUYERS_BROKERAGE',
  'BUYING_BROKER', 'BUYER_BROKER', 'BUYERS_BROKER',
]);
const effectiveClassification = (p) => {
  return BUYING_ACTIVE_ROLES.has((p?.role || '').toUpperCase()) ? 'ACTIVE' : 'REFERENCE';
};

// Normalise any date string to YYYY-MM-DD for the API.
// Parser returns mm/dd/yyyy; date inputs return YYYY-MM-DD.
const normalizeDate = (dateStr) => {
  if (!dateStr) return dateStr;
  const parts = String(dateStr).split('/');
  if (parts.length === 3) {
    const [m, d, y] = parts;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return dateStr; // already ISO or unknown — pass through unchanged
};

// State-aware primary contract label. CO's UI label/value is "Purchase
// Agreement" (previously showed "CBS (Contract to Buy & Sell)" — that text
// no longer appears anywhere in this page). NOTE: the /parse-documents
// backend still only recognizes 'RPA' | 'CBS' | 'COUNTER_OFFER' as file
// labels (see ria.service.js) — toParseApiLabel() below translates CO's
// UI-facing "Purchase Agreement" back to 'CBS' for that one call.
const primaryContractCopy = (licenseState) => {
  if ((licenseState || '').toUpperCase() === 'CO') {
    return { label: 'Purchase Agreement', display: 'Purchase Agreement' };
  }
  return { label: 'RPA', display: 'Purchase Agreement' };
};

const toParseApiLabel = (label, licenseState) =>
  (label === 'Purchase Agreement' && (licenseState || '').toUpperCase() === 'CO') ? 'CBS' : label;

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Action-owner roles for the editable review UI (agents first, then principals).
const OWNER_ROLES = ['BUYER_AGENT', 'SELLER_AGENT', 'BUYER', 'SELLER'];
// Inverse — rebuild a permissions map from a chosen owner list (for persistence).
const permissionsFromOwners = (owners) =>
  Object.fromEntries(OWNER_ROLES.map((r) => [r, owners.includes(r) ? 'ACTIONABLE' : 'READ_ONLY']));

const PurchaseTransactionPage = () => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docusignPendingError, setDocusignPendingError] = useState(false);

  // MLS ID state (shown when status is ACTIVE)
  const [mlsId, setMlsId] = useState('');
  const [isSearchingMls, setIsSearchingMls] = useState(false);
  const [mlsError, setMlsError] = useState('');

  // Form state — fields aligned with POST /api/v1/transactions spec
  const [formData, setFormData] = useState({
    transactionName: '',
    status: '',
    propertyAddress: '',
    propertyCity: '',
    propertyState: '',
    propertyZip: '',
    purchasePrice: '',
    financingType: '',
    expectedClosingDate: '',
  });

  // Success summary
  const [transactionSummary, setTransactionSummary] = useState({
    transactionId: '',
    statusLabel: '',
    fullAddress: '',
  });

  const [zipCodeError, setZipCodeError] = useState('');
  const [userLicenseState, setUserLicenseState] = useState('');
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [formErrors, setFormErrors] = useState({
    transactionName: '',
    status: '',
  });

  // Buying + Active gate state — mirrors mobile CreateTransactionModal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [parsedFilesSignature, setParsedFilesSignature] = useState('');
  const [docsUploaded, setDocsUploaded] = useState(false);
  const [timelineReviewed, setTimelineReviewed] = useState(false);
  const [gateError, setGateError] = useState('');
  const gateRef = useRef(null);

  // Active wizard state (3-step inline flow replacing the old gate)
  const [activeStep, setActiveStep] = useState(1);
  const [inlineFiles, setInlineFiles] = useState([]);
  const [isInlineParsing, setIsInlineParsing] = useState(false);
  const [inlineParseError, setInlineParseError] = useState('');
  const [parsedTimelineEdits, setParsedTimelineEdits] = useState({});
  // Review-screen edits (post-parse, pre-create): action-owner overrides per milestone
  // (key -> array of actionable roles) and which rows are currently in edit mode.
  const [parsedOwnerEdits, setParsedOwnerEdits] = useState({});
  const [partyEmails, setPartyEmails] = useState({});
  // { [idx]: { status: 'idle'|'checking'|'found'|'not_found', userData: object|null } }
  const [emailCheckResults, setEmailCheckResults] = useState({});
  const inlineFileInputRef = useRef(null);
  // Confirmation checkbox — agent must tick before Continue is enabled.
  // Once ticked the upload zone is hidden so no further docs can be added.
  const [docsConfirmed, setDocsConfirmed] = useState(false);

  // TC-only: create-on-behalf-of-agent. TCs never create as the primary agent
  // themselves — they must pick a licensed agent from their contact graph.
  // Mirrors mobile CreateTransactionModal.tsx's isTcCaller/selectedAgent.
  const isTC = (getActiveRoleFromStorage() || '').toUpperCase() === 'TRANSACTION_COORDINATOR';
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentPickerError, setAgentPickerError] = useState('');
  const agentDropdownRef = useRef(null);
  // Tracks whether the agent list has been successfully fetched this page
  // session — lazy-loaded on first dropdown open, cached thereafter, and
  // cleared on fetch failure so the next open click retries.
  const agentsFetchedRef = useRef(false);

  // True when the current status+side combination requires the upload→review gate
  const isBuyingActive = formData.status === 'ACTIVE';
  // Gate is satisfied when not Active, or when both checkboxes are ticked
  const activeGateSatisfied = !isBuyingActive || (docsUploaded && timelineReviewed);

  // Fingerprint of the last submitted file set — used to detect re-uploads
  const filesSignature = (files) =>
    files.map(f => `${f.label}:${f.name}:${f.size || 0}`).join('|');

  // Pre-populate State, City, Zip from logged-in agent's profile
  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const profile = JSON.parse(stored);
        let stateValue = profile.stateCode || profile.state || '';
        // Normalize: if stateValue is a full state name, convert to stateCode
        if (stateValue && stateValue.length > 2) {
          const found = stateCityData.find(
            item => item.state.toLowerCase() === stateValue.toLowerCase()
          );
          stateValue = found ? found.stateCode : '';
        }
        if (stateValue) {
          setFormData(prev => ({
            ...prev,
            propertyState: stateValue,
          }));
        }
        // Derive license state for autocomplete restriction (matches mobile app behaviour)
        let licenseState = profile.licenseState || stateValue;
        if (licenseState && licenseState.length > 2) {
          const found = stateCityData.find(
            item => item.state.toLowerCase() === licenseState.toLowerCase()
          );
          licenseState = found ? found.stateCode : '';
        }
        setUserLicenseState(licenseState.toUpperCase() || '');
      }
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  // Analytics: transaction-create funnel opened
  useEffect(() => {
    track(EVENTS.FUNNEL_STEP, { funnel: 'transaction_create', type: 'purchase', step: 'transaction_create_opened' });
  }, []);

  // Check DocuSign connection on mount — mirrors mobile handleOpenCreateTransaction
  useEffect(() => {
    const activeRole = getActiveRoleFromStorage();
    if (activeRole !== 'AGENT' && activeRole !== 'BROKER') return;
    // CA is excluded from DocuSign account-status checks (same as Dashboard / Transactions pages).
    const agentState = (() => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return (u.licenseState || u.stateCode || u.state || '').toUpperCase();
      } catch { return ''; }
    })();
    // DocuSign is COLORADO-ONLY — skip this DocuSign-connect check for every non-CO state
    // (CA, TX, …), which use Lone Wolf. Was `=== 'CA'` (CA only), which wrongly ran it for TX.
    if (agentState !== 'CO' && agentState !== '') return;

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      unsubscribe();
      try {
        const token = await user.getIdToken(true);
        const response = await apiClient.get(API_ENDPOINTS.docusignConnect, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response?.data || response || {};
        // Check for pending DocuSign invite — mirrors mobile DashboardScreen.tsx
        if (data.connected && data.email) {
          try {
            const usersResp = await apiClient.get(API_ENDPOINTS.docusignRoomsUsers, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const users = usersResp.data?.userSummaries || [];
            const self = users.find(u => (u.email || '').toLowerCase() === (data.email || '').toLowerCase());
            if (self?.status?.toLowerCase() === 'pending') {
              setDocusignPendingError(true);
            }
          } catch {
            // pending check failure is non-fatal — allow create to proceed
          }
        }
      } catch {
        // Silently fall through on error — same as mobile app fallthrough behaviour
      }
    });

    return () => unsubscribe();
  }, []);

  // TC-only: lazy-load the coordinator's brokerage-linked agents (GET /api/v1/tc/agents)
  // to populate the Primary Agent picker. Fetches only on first dropdown open (not on
  // mount), caches for the rest of the page session, and retries on the next open click
  // if the previous attempt failed. Mirrors mobile's CreateTransactionModal.tsx picker.
  const loadAgentOptions = () => {
    if (!isTC || agentsFetchedRef.current || isLoadingAgents) return;
    setIsLoadingAgents(true);
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      unsubscribe();
      try {
        const token = await user.getIdToken(true);
        const agents = await tcService.getLinkedAgents(token);
        setAgentOptions(agents);
        agentsFetchedRef.current = true;
      } catch (e) {
        // Leave agentsFetchedRef false so the next dropdown-open click retries.
      } finally {
        setIsLoadingAgents(false);
      }
    });
  };

  // Close the agent picker dropdown on outside click
  useEffect(() => {
    if (!showAgentDropdown) return;
    const handleClickOutside = (e) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(e.target)) {
        setShowAgentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAgentDropdown]);

  // Reset gate + wizard state when status changes away from Active
  useEffect(() => {
    if (formData.status !== 'ACTIVE') {
      setParsedResult(null);
      setParsedFilesSignature('');
      setDocsUploaded(false);
      setTimelineReviewed(false);
      setShowUploadModal(false);
      setShowReviewModal(false);
    }
    // Always reset wizard to step 1 when status changes
    setActiveStep(1);
    setInlineFiles([]);
    setIsInlineParsing(false);
    setInlineParseError('');
    setParsedTimelineEdits({});
    setParsedOwnerEdits({});
    setPartyEmails({});
    setEmailCheckResults({});
    setDocsConfirmed(false);
  }, [formData.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called by TransactionDocumentUploadModal after a successful parse
  const handleParsed = (result, files) => {
    const sig = filesSignature(files);
    const isResubmit = parsedFilesSignature !== '' && sig !== parsedFilesSignature;
    setParsedResult(result);
    setParsedFilesSignature(sig);
    setDocsUploaded(true);
    setGateError('');
    // Re-upload clears the timeline checkbox to force re-review of the new parse
    if (isResubmit) setTimelineReviewed(false);
    setShowUploadModal(false);
    // Option A (dialog-everywhere): auto-open the review modal when the parse produced
    // milestones — mirrors the Listing flow. Step 2 also lists them, with "Review / Edit" to re-open it.
    if (result?.timeline && result.timeline.length > 0) {
      setShowReviewModal(true);
    }

    // Auto-fill property address from parsed offer documents
    const parsedAddr = result?.propertyAddress;
    if (parsedAddr) {
      const updates = {};
      if (typeof parsedAddr.street === 'string' && parsedAddr.street.trim())
        updates.propertyAddress = parsedAddr.street.trim();
      if (typeof parsedAddr.city === 'string' && parsedAddr.city.trim())
        updates.propertyCity = parsedAddr.city.trim();
      if (typeof parsedAddr.state === 'string' && parsedAddr.state.trim())
        updates.propertyState = parsedAddr.state.trim().toUpperCase();
      if (typeof parsedAddr.zip === 'string' && parsedAddr.zip.trim()) {
        updates.propertyZip = parsedAddr.zip.trim();
        setZipCodeError('');
      }
      if (Object.keys(updates).length > 0)
        setFormData(prev => ({ ...prev, ...updates }));
    }
  };

  // ── Inline wizard handlers ──────────────────────────────────────────────────

  const handleInlineFileAdd = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setInlineFiles(prev => [...prev, {
      name: picked.name,
      size: picked.size,
      mimeType: picked.type || 'application/pdf',
      label: prev.length === 0 ? primaryContractCopy(userLicenseState).label : 'COUNTER_OFFER',
      fileObj: picked,
    }]);
    if (docsUploaded) { setDocsUploaded(false); setParsedResult(null); }
    if (e.target) e.target.value = '';
  };

  const handleInlineFileRemove = (idx) => {
    setInlineFiles(prev => prev.filter((_, i) => i !== idx));
    if (docsUploaded) { setDocsUploaded(false); setParsedResult(null); }
    if (docsConfirmed) setDocsConfirmed(false);
  };

  const handleInlineFileLabel = (idx, label) => {
    setInlineFiles(prev => prev.map((f, i) => i === idx ? { ...f, label } : f));
  };

  const handleInlineContinue = async () => {
    // Validate Transaction Name before touching the parse API
    if (!formData.transactionName.trim()) {
      setFormErrors(prev => ({ ...prev, transactionName: 'Transaction name is required.' }));
      return;
    }
    if (inlineFiles.length === 0) return;
    if (docsUploaded) { setActiveStep(2); return; }
    setIsInlineParsing(true);
    setInlineParseError('');
    try {
      const currentUser = auth.currentUser;
      const firebaseToken = currentUser ? await currentUser.getIdToken(true) : null;
      const parts = inlineFiles.map(f => ({ blob: f.fileObj, name: f.name, label: toParseApiLabel(f.label, userLicenseState) }));
      const result = await parseTransactionDocuments('BUYING', parts, { firebaseToken });
      handleParsed(result, inlineFiles);
      setActiveStep(2);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Document parsing failed. Please try again.';
      setInlineParseError(msg);
    } finally {
      setIsInlineParsing(false);
    }
  };

  const handleInlineDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('pact-drag-over');
    const file = e.dataTransfer.files?.[0];
    if (file) handleInlineFileAdd({ target: { files: [file] }, currentTarget: {} });
  };

  // Step 3 — email lookup on blur (mirrors TransactionChatParties.js checkEmail)
  const handlePartyEmailBlur = async (idx, email) => {
    const trimmed = (email || '').trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailCheckResults(prev => ({ ...prev, [idx]: { status: 'idle', userData: null } }));
      return;
    }
    setEmailCheckResults(prev => ({ ...prev, [idx]: { status: 'checking', userData: null } }));
    try {
      const currentUser = auth.currentUser;
      const firebaseToken = currentUser ? await currentUser.getIdToken(true) : null;
      const response = await apiClient.post(
        API_ENDPOINTS.checkEmail,
        { email: trimmed },
        { headers: { ...(firebaseToken ? { Authorization: `Bearer ${firebaseToken}` } : {}), 'Content-Type': 'application/json' } }
      );
      const resData = response.data;
      let userData = null;
      let userExists = false;
      // Normalise all response shapes — matches TransactionChatParties.js lines 285–301
      if (resData?.user && typeof resData.user === 'object') {
        userData = resData.user; userExists = resData.exists !== false;
      } else if (resData?.data && typeof resData.data === 'object' && !Array.isArray(resData.data)) {
        userData = resData.data; userExists = resData.exists !== false;
      } else if (resData?.firstName || resData?.lastName || resData?.email || resData?.id) {
        userData = resData; userExists = resData.exists !== false;
      }
      setEmailCheckResults(prev => ({
        ...prev,
        [idx]: { status: userExists && userData ? 'found' : 'not_found', userData }
      }));
    } catch {
      // Network/server error — show no chip rather than a misleading "Not on Rialty"
      // Mirrors mobile which renders a neutral state on check failure (#gap2)
      setEmailCheckResults(prev => ({ ...prev, [idx]: { status: 'unknown', userData: null } }));
    }
  };

  // ────────────────────────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'propertyState') {
      setFormData(prev => ({ ...prev, propertyState: value, propertyCity: '', propertyZip: '' }));
      setZipCodeError('');
      setFormErrors(prev => ({ ...prev, propertyState: '', propertyCity: '' }));
    } else if (name === 'propertyZip') {
      setFormData(prev => ({ ...prev, propertyZip: value }));
      if (formData.propertyState && value.trim()) {
        const stateName = getStateNameFromCode(formData.propertyState);
        const validation = validateZipCode(value, stateName);
        setZipCodeError(validation.isValid ? '' : validation.message);
      } else {
        setZipCodeError('');
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddressSelect = (parsed) => {
    setFormData(prev => ({
      ...prev,
      propertyAddress: parsed.street || prev.propertyAddress,
      ...(parsed.stateCode && {
        propertyState: parsed.stateCode,
        propertyCity: parsed.city || '',
        propertyZip: parsed.zip || '',
      }),
    }));
    setFormErrors(prev => ({
      ...prev,
      propertyAddress: '',
      ...(parsed.stateCode && { propertyState: '', propertyCity: '' }),
    }));
    if (parsed.zip) setZipCodeError('');
    setAddressSearchQuery('');
  };

  const searchMlsProperty = async () => {
    if (!mlsId.trim()) {
      setMlsError('Please enter an MLS ID');
      return;
    }
    setIsSearchingMls(true);
    setMlsError('');
    let firebaseToken;
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setMlsError('You are not logged in. Please log in again.');
        setIsSearchingMls(false);
        return;
      }
      firebaseToken = await currentUser.getIdToken(true);
    } catch (tokenError) {
      setMlsError('Authentication error. Please log in again.');
      setIsSearchingMls(false);
      return;
    }
    try {
      const response = await apiClient.get(API_ENDPOINTS.mlsProperty(mlsId.trim()), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      if (response.data) {
        const mlsData = response.data;
        let stateCode = mlsData.propertyState || mlsData.state || '';
        if (stateCode && stateCode.length > 2) {
          const stateData = stateCityData.find((item) => item.state.toLowerCase() === stateCode.toLowerCase());
          if (stateData) stateCode = stateData.stateCode;
        }
        setFormData(prev => ({
          ...prev,
          propertyAddress: mlsData.propertyAddress || mlsData.address || mlsData.streetAddress || '',
          propertyCity: mlsData.propertyCity || mlsData.city || '',
          propertyState: stateCode,
          propertyZip: mlsData.propertyZip || mlsData.zipCode || mlsData.zip || '',
          purchasePrice: mlsData.listPrice || mlsData.price || prev.purchasePrice,
        }));
      }
    } catch (error) {
      setMlsError(error.response?.data?.message || 'Property not found. Please check the MLS ID and try again.');
    } finally {
      setIsSearchingMls(false);
    }
  };

  const createTransaction = async () => {
    track(EVENTS.FUNNEL_STEP, { funnel: 'transaction_create', type: 'purchase', step: 'transaction_create_submitted' });

    // Gate guard — Buying+Active requires upload+review before create
    if (isBuyingActive && !activeGateSatisfied) {
      setGateError(!docsUploaded
        ? 'Please upload the documents.'
        : 'Please review the timeline.');
      gateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setGateError('');

    // Validate required fields
    const errors = { transactionName: '', status: '' };
    let hasErrors = false;
    if (!formData.transactionName.trim()) { errors.transactionName = 'This field is required'; hasErrors = true; }
    if (!formData.status) { errors.status = 'This field is required'; hasErrors = true; }
    if (hasErrors) { setFormErrors(errors); return; }

    // TC-only: a Primary Agent must be selected before submit — TCs cannot be
    // the primary agent themselves. Mirrors mobile's isTcCaller/selectedAgent
    // required-field alert.
    if (isTC && !selectedAgent?.id) {
      setAgentPickerError('Select the licensed agent this transaction is being created for. Transaction Coordinators cannot be the primary agent.');
      return;
    }
    setAgentPickerError('');

    // Validate ZIP only when both state and zip are provided
    if (formData.propertyZip && formData.propertyState) {
      const stateName = getStateNameFromCode(formData.propertyState);
      const zipValidation = validateZipCode(formData.propertyZip, stateName);
      if (!zipValidation.isValid) { setZipCodeError(zipValidation.message); return; }
    }

    setIsSubmitting(true);

    let firebaseToken;
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) { alert('You are not logged in. Please log in again.'); navigate('/login'); return; }
      firebaseToken = await currentUser.getIdToken(true);
    } catch (tokenError) {
      alert('Authentication error. Please log in again.');
      navigate('/login');
      return;
    }

    // Build request body aligned with API spec
    const requestBody = {
      type: 'PURCHASE',
      side: 'BUYING',
      transactionName: formData.transactionName,
    };
    if (formData.propertyAddress.trim()) requestBody.propertyAddress = formData.propertyAddress;
    if (formData.propertyCity) requestBody.propertyCity = formData.propertyCity;
    if (formData.propertyState) requestBody.propertyState = formData.propertyState;
    if (formData.status) requestBody.status = formData.status;
    if (formData.propertyZip) requestBody.propertyZip = formData.propertyZip;
    if (formData.purchasePrice) requestBody.purchasePrice = parseFloat(formData.purchasePrice);
    if (formData.financingType) requestBody.financingType = formData.financingType;
    if (formData.expectedClosingDate) requestBody.expectedClosingDate = formData.expectedClosingDate;
    // TC-driven create: send the selected licensed agent as primaryAgentId.
    // Agents don't set this — backend defaults primaryAgentId to the caller.
    if (isTC && selectedAgent?.id) requestBody.primaryAgentId = selectedAgent.id;
    // Buying+Active: send AI-parsed timeline and parties (with agent-entered emails)
    if (isBuyingActive && parsedResult) {
      // Normalize all timeline dates to YYYY-MM-DD — parser returns mm/dd/yyyy,
      // date inputs return YYYY-MM-DD; mixing formats breaks backend persistence (#gap9)
      if (parsedResult.timeline) {
        requestBody.timeline = parsedResult.timeline.map(item => {
          const key = item.type || item.name;
          const editedDate = parsedTimelineEdits[key];
          const dueDate = editedDate || normalizeDate(item.dueDate || item.deadline);
          const ownerEdit = parsedOwnerEdits[key];
          return {
            ...item,
            ...(dueDate ? { dueDate } : {}),
            // Apply any review-screen action-owner override onto the permissions map.
            ...(ownerEdit ? { permissions: permissionsFromOwners(ownerEdit) } : {}),
          };
        });
      }
      // Merge party emails; promote brokerage roles to ACTIVE (#gap3)
      if (parsedResult.parties) {
        requestBody.parties = parsedResult.parties.map((p, idx) => ({
          ...p,
          classification: effectiveClassification(p),
          ...(partyEmails[idx] !== undefined ? { email: partyEmails[idx] } : {}),
        }));
      }
      // Map the labeled file list to Offer-chain metadata so the backend
      // materialises PA_V1 / Counter_V{n} Offer rows during direct-active-
      // create. RPA/CBS → PURCHASE_AGREEMENT, COUNTER_OFFER → COUNTER_OFFER,
      // anything else (OTHER, pre-approval, …) stays as a Docs-tab upload
      // and doesn't become an Offer row.
      const offerDocsMetadata = inlineFiles
        .map((f, idx) => {
          if (f.label === 'RPA' || f.label === 'CBS') {
            return { fileName: f.name, documentType: 'PURCHASE_AGREEMENT', sortOrder: idx + 1 };
          }
          if (f.label === 'COUNTER_OFFER') {
            return { fileName: f.name, documentType: 'COUNTER_OFFER', sortOrder: idx + 1 };
          }
          return null;
        })
        .filter(Boolean);
      if (offerDocsMetadata.length > 0) {
        requestBody.uploadedOfferDocuments = offerDocsMetadata;
      }
    }

    console.log('Creating purchase transaction:', requestBody);

    try {
      const response = await apiClient.post(API_ENDPOINTS.transactions, requestBody, {
        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
        // Create-transaction is a heavy synchronous path (DocuSign room creation, optional
        // Lone Wolf transaction+contacts creation) — observed 27-30s+ server-side, and the
        // gateway route itself allows up to 60s. Override the shared client's 30s default so
        // this call isn't aborted client-side before a legitimately slow-but-successful
        // response comes back.
        timeout: 90000,
      });

      console.log('Purchase transaction created:', response.data);

      const transactionId = response.data?.id || response.data?.transactionId;

      track(EVENTS.FUNNEL_STEP, { funnel: 'transaction_create', type: 'purchase', step: 'transaction_create_succeeded' });

      // TC-only: explicitly add the TC as a participant on the transaction they
      // just created. The backend records the TC as coordinatorUserId (not a
      // participant row), and the transaction-list query filters by participant
      // role — without this, the transaction never shows up in the TC's own
      // "My Transactions" list even though it correctly shows for the agent.
      // Best-effort: a failure here doesn't block navigation.
      if (transactionId && isTC) {
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          const name = [stored.firstName, stored.lastName].filter(Boolean).join(' ') || stored.email || 'Transaction Coordinator';
          await apiClient.post(
            API_ENDPOINTS.transactionParticipants(transactionId),
            {
              userId: stored.id || stored.uid || stored.firebaseUid,
              name,
              email: stored.email,
              role: 'TRANSACTION_COORDINATOR',
            },
            { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
          );
          console.log('TC added as participant');
        } catch (participantError) {
          console.error('Error adding TC as participant:', participantError);
        }
      }

      // Guard AI-parsed milestones from being overwritten by TransactionDetailPage's
      // autoApplyTemplate (which deletes milestones that have no metadata.roleAccess).
      // Set AFTER the DocuSign check so we never mark a deleted transaction as AI-parsed.
      // Mirrors the same key set by handleConversionReviewed for prospect→active conversions.
      if (isBuyingActive && parsedResult?.timeline?.length && transactionId) {
        try { localStorage.setItem(`txn_ai_timeline_${transactionId}`, '1'); } catch {}
      }

      // Apply milestone template — skip for Active (timeline already in create payload)
      // Mirrors mobile DashboardScreen.tsx isBuyingActiveCreate guard (#gap5)
      if (transactionId && !isBuyingActive) {
        try {
          const ft = formData.financingType?.toLowerCase();
          const templateName = ft === 'cash' ? 'CASH_PURCHASE' : ft === 'fha' ? 'FHA_PURCHASE' : 'STANDARD_PURCHASE';
          await apiClient.post(
            API_ENDPOINTS.applyMilestoneTemplate(transactionId),
            { templateName },
            { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
          );
          console.log(`${templateName} milestone template applied`);
        } catch (milestoneError) {
          console.error('Error applying milestone template:', milestoneError);
        }
      }

      // Upload wizard files to DocsTab — mirrors mobile uploadWizardFilesToDocTab (#gap6)
      // Parallel + allSettled so one failing file never blocks navigation.
      if (isBuyingActive && inlineFiles.length > 0 && transactionId) {
        const { label: primaryLabel, display: primaryDisplay } = primaryContractCopy(userLicenseState);
        const labelToTag = (label) => {
          if (label === primaryLabel) return primaryDisplay;
          if (label === 'COUNTER_OFFER') return 'Counter Offer';
          return 'Other';
        };
        await Promise.allSettled(
          inlineFiles.map(async (f) => {
            try {
              const taggedName = `[${labelToTag(f.label)}] ${f.name}`;
              const fd = new FormData();
              fd.append('file', f.fileObj, taggedName);
              fd.append('folder', 'documents');
              fd.append('name', taggedName);
              await apiClient.post(
                API_ENDPOINTS.uploadTransactionFile(transactionId),
                fd,
                { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'multipart/form-data' } }
              );
            } catch (uploadErr) {
              console.warn(`[Wizard docTab upload] failed for ${f.name}:`, uploadErr);
            }
          })
        );
      }

      const stateName = getStateNameFromCode(formData.propertyState);
      const fullAddress = [formData.propertyAddress, formData.propertyCity, stateName, formData.propertyZip]
        .filter(Boolean).join(', ');
      const statusLabel = STATUS_OPTIONS.find(o => o.value === formData.status)?.label || formData.status || 'Not specified';

      setTransactionSummary({
        transactionId: transactionId || 'TXN-' + Date.now(),
        statusLabel,
        fullAddress,
      });

      setShowSuccess(true);

      // Determine filter based on transaction status
      const filter = formData.status === 'ACTIVE' ? 'active' : 'prospect';

      // Navigate to transactions list with appropriate filter after 3 seconds
      setTimeout(() => {
        navigate('/transactions', { state: { filter } });
      }, 3000);

    } catch (error) {
      console.error('Error creating purchase transaction:', error);
      // 503 gateway-fallback guard — mirrors mobile CreateTransactionModal.tsx (#gap4)
      // A 503 with a /fallback/ path means the gateway timed out but the backend
      // may have already committed the transaction. Warn the agent to check their
      // list before retrying to avoid creating a duplicate.
      const httpStatus = error?.response?.status;
      const fallbackPath = error?.response?.data?.path;
      const isGatewayFallback = httpStatus === 503
        && typeof fallbackPath === 'string'
        && fallbackPath.startsWith('/fallback/');
      if (isGatewayFallback) {
        alert("We didn't get a response in time, but your transaction may have been saved. Please check your Transactions list before trying again — retrying now could create a duplicate.");
      } else {
        alert(error.response?.data?.message || 'Failed to create purchase transaction. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewTransaction = () => {
    const filter = formData.status === 'ACTIVE' ? 'active' : 'prospect';
    navigate('/transactions', { state: { filter } });
  };

  return (
    <div className="purchase-transaction-container">

      {/* DocuSign Invitation Pending Modal (#gap7) */}
      {docusignPendingError && (
        <div className="docusign-error-overlay">
          <div className="docusign-error-modal">
            <div className="docusign-error-icon" style={{background:'#F59E0B'}}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3 className="docusign-error-title">Invitation Pending</h3>
            <p className="docusign-error-message">
              Your DocuSign invitation is still pending. Please accept the invitation sent to your email before creating a transaction.
            </p>
            <div className="docusign-error-actions">
              <button className="docusign-error-btn-dismiss" onClick={() => setDocusignPendingError(false)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buying+Active: Document Upload Modal */}
      <TransactionDocumentUploadModal
        visible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onParsed={handleParsed}
      />

      {/* Buying+Active: Timeline Review Modal */}
      <TransactionReviewModal
        visible={showReviewModal}
        side="BUYING"
        parsed={parsedResult}
        onReviewed={(edited) => { if (Array.isArray(edited)) setParsedResult(prev => prev ? { ...prev, timeline: edited } : prev); setTimelineReviewed(true); setGateError(''); setShowReviewModal(false); }}
        onCancel={() => setShowReviewModal(false)}
      />

      <div className="purchase-main-content">
        <div className="purchase-content-box">
        {/* FORM SCREEN */}
        {!showSuccess && (
          <div className="purchase-form-screen">
            <div className="purchase-page-header">
              <h1 className="purchase-page-title">Create Buying Transaction</h1>
              <p className="purchase-page-subtitle">{isBuyingActive ? 'Status: Active — purchase agreement required to proceed' : 'Start a new purchase transaction for your buyer client'}</p>
            </div>

            {/* TC-only: Primary Agent picker. TCs create on behalf of a licensed
                agent from their contact graph — shown once, ahead of both the
                Active wizard and the plain form below. */}
            {isTC && (
              <div className="purchase-form-group purchase-agent-picker" ref={agentDropdownRef}>
                <label className="purchase-form-label">Primary Agent *</label>
                <div
                  className={`purchase-form-input purchase-agent-picker-trigger${agentPickerError ? ' purchase-input-error' : ''}`}
                  onClick={() => {
                    setShowAgentDropdown(v => !v);
                    loadAgentOptions();
                  }}
                >
                  {selectedAgent ? (
                    <div className="purchase-agent-picker-row">
                      <div className="purchase-agent-picker-avatar">
                        {selectedAgent.profilePhotoUrl ? (
                          <img src={selectedAgent.profilePhotoUrl} alt="" className="purchase-agent-picker-avatar-img" />
                        ) : (
                          <span className="purchase-agent-picker-avatar-initials">
                            {(`${selectedAgent.firstName || ''} ${selectedAgent.lastName || ''}`.trim() || selectedAgent.email || '?')
                              .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        )}
                      </div>
                      <div className="purchase-agent-picker-text">
                        <span className="purchase-agent-picker-name">
                          {`${selectedAgent.firstName || ''} ${selectedAgent.lastName || ''}`.trim() || selectedAgent.email}
                          {selectedAgent.licenseState ? ` · ${selectedAgent.licenseState}` : ''}
                        </span>
                        {selectedAgent.email && <span className="purchase-agent-picker-email">{selectedAgent.email}</span>}
                      </div>
                    </div>
                  ) : (
                    <span className="purchase-agent-picker-placeholder">
                      {isLoadingAgents ? 'Loading agents…' : 'Select the agent this transaction is for'}
                    </span>
                  )}
                  <svg className="purchase-agent-picker-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points={showAgentDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                  </svg>
                </div>
                {showAgentDropdown && (
                  <div className="purchase-agent-picker-list">
                    {isLoadingAgents && (
                      <div className="purchase-agent-picker-loading">
                        <span className="purchase-agent-picker-spinner" />
                        <span>Loading agents…</span>
                      </div>
                    )}
                    {agentOptions.length === 0 && !isLoadingAgents && (
                      <div className="purchase-agent-picker-empty">No agents found in your brokerage yet. If your firm has just registered, ask an agent to sign up and link to it.</div>
                    )}
                    {agentOptions.map((agent) => {
                      const displayName = `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email;
                      const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
                      return (
                        <div
                          key={agent.id || agent.firebaseUid || agent.email}
                          className={`purchase-agent-picker-item${selectedAgent?.id === agent.id ? ' active' : ''}`}
                          onClick={() => { setSelectedAgent(agent); setShowAgentDropdown(false); setAgentPickerError(''); }}
                        >
                          <div className="purchase-agent-picker-avatar">
                            {agent.profilePhotoUrl ? (
                              <img src={agent.profilePhotoUrl} alt="" className="purchase-agent-picker-avatar-img" />
                            ) : (
                              <span className="purchase-agent-picker-avatar-initials">{initials}</span>
                            )}
                          </div>
                          <div className="purchase-agent-picker-text">
                            <span className="purchase-agent-picker-name">
                              {displayName}{agent.licenseState ? ` · ${agent.licenseState}` : ''}
                            </span>
                            {agent.email && <span className="purchase-agent-picker-email">{agent.email}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {agentPickerError && <span className="purchase-field-error">{agentPickerError}</span>}
              </div>
            )}

            {/* ── Active status: 3-step wizard ── */}
            {isBuyingActive && (
              <div className="pact-wizard">

                {/* Step progress bar */}
                <div className="pact-step-bar">
                  {['Details & upload', 'Review parsed data', 'Add party emails'].map((label, i) => {
                    const n = i + 1;
                    const done = activeStep > n;
                    const active = activeStep === n;
                    return (
                      <React.Fragment key={n}>
                        {i > 0 && <div className={`pact-step-line${done ? ' done' : ''}`} />}
                        <div className="pact-step-item">
                          <div className={`pact-step-num${active ? ' active' : done ? ' done' : ''}`}>
                            {done
                              ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                              : n}
                          </div>
                          <span className={`pact-step-label${active ? ' active' : done ? ' done' : ''}`}>{label}</span>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* ── STEP 1: Transaction details + document upload ── */}
                {activeStep === 1 && (
                  <div className="pact-card">
                    <div className="pact-sec-head">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                      Transaction details
                    </div>

                    <div className="pact-row2">
                      <div className="purchase-form-group">
                        <label className="purchase-form-label">Transaction Name *</label>
                        <input
                          type="text"
                          className={`purchase-form-input${formErrors.transactionName ? ' purchase-input-error' : ''}`}
                          name="transactionName"
                          value={formData.transactionName}
                          onChange={handleInputChange}
                          placeholder="Enter transaction name"
                        />
                        {formErrors.transactionName && <span className="purchase-field-error">{formErrors.transactionName}</span>}
                      </div>
                      <div className="purchase-form-group">
                        <label className="purchase-form-label">Status *</label>
                        <select
                          className={`purchase-form-input purchase-form-select pact-active-sel${formErrors.status ? ' purchase-input-error' : ''}`}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="">Select status...</option>
                          {STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="pact-divider" />

                    <div className="pact-sec-head">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Documents
                    </div>

                    <div className="pact-banner pact-banner-purple">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3d2d8f" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      <p><strong>Active status requires a signed purchase agreement.</strong> Upload it below — we'll extract parties, property address and all timeline deadlines automatically. Counter offers and other supporting documents can also be added.</p>
                    </div>

                    {/* Upload zone — hidden once the confirmation checkbox is ticked */}
                    {!isInlineParsing && !docsConfirmed && (
                      <div
                        className="pact-upload-zone"
                        onClick={() => inlineFileInputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('pact-drag-over'); }}
                        onDragLeave={e => e.currentTarget.classList.remove('pact-drag-over')}
                        onDrop={handleInlineDrop}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7F77DD" strokeWidth="1.5" strokeLinecap="round" style={{margin:'0 auto 10px',display:'block'}}>
                          <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                        </svg>
                        <div className="pact-uz-title">{inlineFiles.length > 0 ? 'Add another document' : 'Upload purchase agreement'}</div>
                        <div className="pact-uz-sub">Drag &amp; drop or <span>browse files</span> · PDF · Max 25 MB</div>
                      </div>
                    )}

                    {/* Uploaded file list */}
                    {inlineFiles.length > 0 && !isInlineParsing && (
                      <div className="pact-file-list">
                        {inlineFiles.map((f, idx) => (
                          <div key={`${f.name}-${idx}`} className="pact-file-row">
                            <div className={`pact-file-icon${f.label === 'COUNTER_OFFER' ? ' amber' : f.label === 'OTHER' ? ' gray' : ''}`}>
                              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                              </svg>
                            </div>
                            <div className="pact-file-info">
                              <div className="pact-file-name" title={f.name}>{f.name}</div>
                              <div className="pact-file-meta">{formatFileSize(f.size)}</div>
                            </div>
                            <select className="pact-file-label-select" value={f.label} onChange={e => handleInlineFileLabel(idx, e.target.value)}>
                              {/* Primary label is state-aware: CA value/display='RPA'/'Purchase Agreement', CO value/display='Purchase Agreement' (#gap1) */}
                              <option value={primaryContractCopy(userLicenseState).label}>{primaryContractCopy(userLicenseState).display}</option>
                              <option value="COUNTER_OFFER">Counter offer</option>
                              <option value="OTHER">Other</option>
                            </select>
                            <button className="pact-file-remove" onClick={() => handleInlineFileRemove(idx)} title="Remove">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Confirmation checkbox — shown when at least one file is staged.
                        Ticking locks the upload zone; unticking reopens it and resets the parse. */}
                    {inlineFiles.length > 0 && !isInlineParsing && (
                      <label className="pact-confirm-check">
                        <input
                          type="checkbox"
                          checked={docsConfirmed}
                          onChange={e => {
                            const checked = e.target.checked;
                            setDocsConfirmed(checked);
                            if (!checked) {
                              setDocsUploaded(false);
                              setParsedResult(null);
                            }
                          }}
                        />
                        <span className="pact-confirm-check-label">
                          <strong>I confirm there are no more documents to upload.</strong>{' '}
                          All purchase agreements, counter offers, and supporting documents have been added above.
                        </span>
                      </label>
                    )}

                    {/* Processing */}
                    {isInlineParsing && (
                      <div className="pact-processing-box">
                        <svg className="pact-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3d2d8f" strokeWidth="2.5" strokeLinecap="round" style={{flexShrink:0}}>
                          <path d="M12 2a10 10 0 0 1 10 10"/>
                        </svg>
                        <div>
                          <div className="pact-processing-text">Processing documents…</div>
                          <div className="pact-processing-sub">Extracting parties, property address and timeline deadlines</div>
                        </div>
                      </div>
                    )}

                    {/* Error */}
                    {inlineParseError && !isInlineParsing && (
                      <div className="pact-error-banner">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0}}>
                          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span>{inlineParseError}</span>
                      </div>
                    )}

                    {/* Parsed success */}
                    {docsUploaded && !isInlineParsing && (
                      <div className="pact-banner pact-banner-green">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:1}}>
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        <p><strong>Documents processed successfully.</strong> Click Continue to review extracted data before creating the transaction.</p>
                      </div>
                    )}

                    <input ref={inlineFileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{display:'none'}} onChange={handleInlineFileAdd} />

                    <div className="pact-btn-row">
                      <button className="pact-btn-back" onClick={() => navigate('/dashboard')} disabled={isInlineParsing}>Cancel</button>
                      <button
                        className="pact-btn-primary"
                        onClick={handleInlineContinue}
                        disabled={inlineFiles.length === 0 || isInlineParsing || !docsConfirmed}
                      >
                        {isInlineParsing
                          ? 'Processing…'
                          : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>Continue</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 2: Review parsed data ── */}
                {activeStep === 2 && (
                  <div className="pact-card">
                    <div className="pact-banner pact-banner-green">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:1}}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <p><strong>Auto-filled from purchase agreement.</strong> Review all fields and correct if needed before continuing. All fields are editable.</p>
                    </div>

                    <div className="pact-sec-head">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      Property address
                    </div>

                    <div className="purchase-form-group">
                      <label className="purchase-form-label">Street Address *</label>
                      <input type="text" className="purchase-form-input pact-filled" name="propertyAddress" value={formData.propertyAddress} onChange={handleInputChange} placeholder="Auto-filled from offer documents" />
                    </div>
                    <div className="pact-row3">
                      <div className="purchase-form-group">
                        <label className="purchase-form-label">City</label>
                        <input type="text" className="purchase-form-input pact-filled" name="propertyCity" value={formData.propertyCity} onChange={handleInputChange} placeholder="City" />
                      </div>
                      <div className="purchase-form-group">
                        <label className="purchase-form-label">State</label>
                        <input type="text" className="purchase-form-input pact-filled" value={getStateNameFromCode(formData.propertyState) || formData.propertyState} readOnly />
                      </div>
                      <div className="purchase-form-group">
                        <label className="purchase-form-label">Zip Code</label>
                        <input type="text" className={`purchase-form-input pact-filled${zipCodeError ? ' purchase-input-error' : ''}`} name="propertyZip" value={formData.propertyZip} onChange={handleInputChange} placeholder="ZIP" maxLength="10" />
                        {zipCodeError && <span className="purchase-zip-error">{zipCodeError}</span>}
                      </div>
                    </div>

                    {/* Parsed timeline summary — same card as the Listing create flow. Read-only
                        list with action owners; "Review / Edit" re-opens the editable review modal.
                        Stays visible after "Reviewed" and when coming Back from step 3, so the agent
                        can always revisit the generated timelines. */}
                    {parsedResult?.timeline && parsedResult.timeline.length > 0 && (
                    <div className="pact-keydates-card">
                      <div className="pact-sec-head" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          Timeline milestones ({parsedResult.timeline.length})
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowReviewModal(true)}
                          style={{ background: 'none', border: '1px solid #D1D5DB', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: '#5B47D6', cursor: 'pointer' }}
                        >
                          Review / Edit
                        </button>
                      </div>
                      {parsedResult.timeline.map((m, idx) => {
                        const hasDate = !!m.dueDate;
                        const liveTitle = m?.title ?? m?.name ?? m?.type ?? '—';
                        // Action owner = the single role the parser/agent marked ACTIONABLE.
                        const owner = (() => {
                          const p = m?.permissions;
                          if (!p || typeof p !== 'object') return null;
                          const order = ['BUYER_AGENT', 'SELLER_AGENT', 'BUYER', 'SELLER'];
                          const labels = { BUYER_AGENT: "Buyer's Agent", SELLER_AGENT: "Seller's Agent", BUYER: 'Buyer', SELLER: 'Seller' };
                          const r = order.find(k => String(p[k]).toUpperCase() === 'ACTIONABLE');
                          return r ? labels[r] : null;
                        })();
                        return (
                          <div key={`${m.type}-${idx}`} className="pact-keydate-row">
                            <span className="pact-keydate-label">
                              {liveTitle}
                              {owner && <span style={{ color: '#5B47D6', fontWeight: 600, fontSize: 11 }}> · {owner}</span>}
                            </span>
                            <span className={`pact-keydate-value${hasDate ? '' : ' pending'}`}>
                              {hasDate ? m.dueDate : 'Date pending'}
                            </span>
                          </div>
                        );
                      })}
                      <p className="pact-keydates-footer">
                        The full timeline will populate the transaction after creation. Items showing "Date pending" can be set later from the timeline tab.
                      </p>
                    </div>
                    )}

                    <div className="pact-btn-row">
                      <button className="pact-btn-back" onClick={() => setActiveStep(1)}>Back</button>
                      <button className="pact-btn-primary" onClick={() => { setTimelineReviewed(true); setGateError(''); setActiveStep(3); }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        Confirm &amp; add parties
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 3: Add party emails ── */}
                {activeStep === 3 && (
                  <div className="pact-card">
                    <div className="pact-banner pact-banner-purple">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3d2d8f" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      <p><strong>Names parsed from purchase agreement.</strong> Enter each active party's email — we'll check if they're already on Rialty. Reference party names are shown for record only; emails can be added later.</p>
                    </div>

                    {/* Active parties — effectiveClassification derives ACTIVE from role, not the AI's classification field */}
                    {(parsedResult?.parties || []).some(p => effectiveClassification(p) === 'ACTIVE') && (
                      <>
                        <div className="pact-section-label">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="#3d2d8f"><circle cx="12" cy="12" r="10"/></svg>
                          Active parties
                          <span className="pact-section-sublabel">· buyers · will be added to your contacts</span>
                        </div>
                        {(parsedResult?.parties || [])
                          .map((party, originalIdx) => ({ party, originalIdx }))
                          .filter(({ party }) => effectiveClassification(party) === 'ACTIVE')
                          .map(({ party, originalIdx }) => {
                            const check = emailCheckResults[originalIdx];
                            const emailVal = partyEmails[originalIdx] !== undefined ? partyEmails[originalIdx] : (party.email || '');
                            return (
                              <div key={originalIdx} className="pact-party-card pact-active-p">
                                <div className="pact-party-avatar pact-av-purple">
                                  {(party.name || '?').split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase()}
                                </div>
                                <div className="pact-party-info">
                                  <div className="pact-party-name">{party.name}</div>
                                  <div className="pact-party-role">{party.role || 'Buyer'}</div>
                                </div>
                                <div className="pact-party-email-col">
                                  {/* Email input + status chip row */}
                                  <div className="pact-email-wrap">
                                    <input
                                      type="email"
                                      className={`pact-party-email-input${check?.status === 'found' ? ' pact-email-success' : check?.status === 'not_found' ? ' pact-email-error' : ''}`}
                                      placeholder="Enter email address (optional)"
                                      value={emailVal}
                                      readOnly={check?.status === 'found'}
                                      onChange={e => {
                                        setPartyEmails(prev => ({ ...prev, [originalIdx]: e.target.value }));
                                        if (check?.status !== 'idle') setEmailCheckResults(prev => ({ ...prev, [originalIdx]: { status: 'idle', userData: null } }));
                                      }}
                                      onBlur={e => handlePartyEmailBlur(originalIdx, e.target.value)}
                                    />
                                    {check?.status === 'checking' && (
                                      <span className="pact-email-chip pact-chip-checking">Checking…</span>
                                    )}
                                    {check?.status === 'found' && (
                                      <span className="pact-email-chip pact-chip-found">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                        On Rialty
                                      </span>
                                    )}
                                    {/* 'unknown' = network error → show no chip to avoid misleading (#gap2) */}
                                    {check?.status === 'not_found' && (
                                      <span className="pact-email-chip pact-chip-new">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                                        Not on Rialty
                                      </span>
                                    )}
                                  </div>
                                  {/* Status note */}
                                  {check?.status === 'found' && (
                                    <div className="pact-email-note pact-note-green">
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                      Will be added to your My Contacts automatically
                                    </div>
                                  )}
                                  {check?.status === 'not_found' && (
                                    <div className="pact-email-note pact-note-red">
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                      Invite will be sent on transaction creation. They'll join as Buyer and be added to your contacts.
                                    </div>
                                  )}
                                  {(!check || check.status === 'idle' || check.status === 'unknown') && (
                                    <div className="pact-email-note">Email optional — can be added from the transaction later</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </>
                    )}

                    {/* Reference parties (sellers, listing agents) */}
                    {(parsedResult?.parties || []).some(p => effectiveClassification(p) === 'REFERENCE') && (
                      <>
                        <div className="pact-divider" />
                        <div className="pact-section-label">
                          <svg width="7" height="7" viewBox="0 0 24 24" fill="#bbb"><circle cx="12" cy="12" r="10"/></svg>
                          Reference parties
                          <span className="pact-section-sublabel">· sellers, listing agents — names shown for record only</span>
                        </div>
                        {(parsedResult?.parties || [])
                          .filter(p => effectiveClassification(p) === 'REFERENCE')
                          .map((party, idx) => (
                            <div key={idx} className="pact-party-card pact-ref-p">
                              <div className="pact-party-avatar pact-av-gray">
                                {(party.name || '?').split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase()}
                              </div>
                              <div className="pact-party-info">
                                <div className="pact-party-name">{party.name}</div>
                                <div className="pact-party-role">{party.role}</div>
                              </div>
                              <div className="pact-ref-note">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                Email can be added later from the transaction
                              </div>
                            </div>
                          ))}
                      </>
                    )}

                    {(!parsedResult?.parties || parsedResult.parties.length === 0) && (
                      <p className="pact-no-parties">No parties found in the parsed documents. You can add parties after the transaction is created.</p>
                    )}

                    <div className="pact-btn-row">
                      <button className="pact-btn-back" onClick={() => setActiveStep(2)}>Back</button>
                      <button className="pact-btn-primary" onClick={createTransaction} disabled={isSubmitting}>
                        {isSubmitting
                          ? 'Creating…'
                          : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>Create transaction</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Non-active status: existing form (unchanged) ── */}
            {!isBuyingActive && (
            <div className="purchase-form-card">
              <div className="purchase-form-section">
                <div className="purchase-form-section-header">
                  🏠 Property Information
                </div>

                <div className="purchase-form-group">
                  <label className="purchase-form-label">Transaction Name *</label>
                  <input
                    type="text"
                    className={`purchase-form-input ${formErrors.transactionName ? 'purchase-input-error' : ''}`}
                    name="transactionName"
                    value={formData.transactionName}
                    onChange={handleInputChange}
                    placeholder="Enter transaction name"
                  />
                  {formErrors.transactionName && (
                    <span className="purchase-field-error">{formErrors.transactionName}</span>
                  )}
                </div>

                <div className="purchase-form-group">
                  <label className="purchase-form-label">Status *</label>
                  <select
                    className={`purchase-form-input purchase-form-select ${formErrors.status ? 'purchase-input-error' : ''}`}
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="">Select status...</option>
                    {STATUS_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {formErrors.status && (
                    <span className="purchase-field-error">{formErrors.status}</span>
                  )}
                </div>

                {/* Buying+Active gate: Upload → AI parse → Review → Create
                    Shown above Property Address, matching mobile inline gate position */}
                {isBuyingActive && (
                  <div className="purchase-active-gate" ref={gateRef}>
                    <button
                      type="button"
                      className="purchase-gate-upload-btn"
                      onClick={() => setShowUploadModal(true)}
                      disabled={isSubmitting}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="12" y1="11" x2="12" y2="17"/>
                        <line x1="9" y1="14" x2="15" y2="14"/>
                      </svg>
                      {docsUploaded ? 'Re-upload documents' : 'Upload'}
                    </button>

                    {/* Checkbox 1 — read-only, auto-ticks after successful upload */}
                    <div className="purchase-gate-check-row">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                        {docsUploaded
                          ? <><rect x="3" y="3" width="18" height="18" rx="2" fill="#2D1B69" stroke="#2D1B69"/><polyline points="9 12 11 14 15 10" stroke="white" strokeWidth="2.5" fill="none"/></>
                          : <rect x="3" y="3" width="18" height="18" rx="2" stroke="#9CA3AF" fill="none"/>
                        }
                      </svg>
                      <span className="purchase-gate-check-text">{docsUploaded ? 'Documents uploaded' : 'Documents upload'}</span>
                    </div>

                    {/* Checkbox 2 — tappable only after checkbox 1; opens timeline review */}
                    <button
                      type="button"
                      className="purchase-gate-check-row purchase-gate-check-btn"
                      onClick={() => { if (docsUploaded) setShowReviewModal(true); }}
                      disabled={!docsUploaded || isSubmitting}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                        {timelineReviewed
                          ? <><rect x="3" y="3" width="18" height="18" rx="2" fill="#2D1B69" stroke="#2D1B69"/><polyline points="9 12 11 14 15 10" stroke="white" strokeWidth="2.5" fill="none"/></>
                          : <rect x="3" y="3" width="18" height="18" rx="2" stroke="#9CA3AF" fill="none"/>
                        }
                      </svg>
                      <span className={`purchase-gate-check-text${!docsUploaded ? ' purchase-gate-check-text-disabled' : ''}`}>
                        {timelineReviewed ? 'Timeline reviewed' : 'Timeline review'}
                      </span>
                    </button>
                    {gateError && (
                      <p className="purchase-gate-error">{gateError}</p>
                    )}
                  </div>
                )}

                {/* Property Address Section */}
                <div className="purchase-property-address-section">
                  <div className="purchase-property-address-header">
                    <span className="purchase-property-address-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    </span>
                    <span className="purchase-property-address-title">Property Address</span>
                  </div>

                  {/* MLS ID (Optional) — hidden per product request, keep code for future re-enable */}
                  {false && (
                    <div className="purchase-form-group">
                      <label className="purchase-form-label purchase-optional">MLS ID (Optional)</label>
                      <div className="purchase-mls-input-wrapper">
                        <input
                          type="text"
                          className={`purchase-form-input purchase-mls-input ${mlsError ? 'purchase-input-error' : ''}`}
                          value={mlsId}
                          onChange={(e) => { setMlsId(e.target.value); setMlsError(''); }}
                          placeholder="Enter MLS ID"
                        />
                        <button
                          type="button"
                          className="purchase-mls-search-btn"
                          onClick={searchMlsProperty}
                          disabled={isSearchingMls}
                        >
                          {isSearchingMls ? <span className="purchase-mls-spinner"></span> : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          )}
                        </button>
                      </div>
                      {mlsError && <span className="purchase-mls-error">{mlsError}</span>}
                    </div>
                  )}

                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">Search Property Address (Optional)</label>
                    <AddressAutocomplete
                      value={addressSearchQuery}
                      onChange={setAddressSearchQuery}
                      onAddressSelect={handleAddressSelect}
                      placeholder="Search property address..."
                      inputClassName="purchase-form-input"
                      restrictToStateCode={userLicenseState || undefined}
                    />
                  </div>

                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">Street Address (Optional)</label>
                    <input
                      type="text"
                      className={`purchase-form-input ${formErrors.propertyAddress ? 'purchase-input-error' : ''}`}
                      name="propertyAddress"
                      value={formData.propertyAddress}
                      onChange={handleInputChange}
                      placeholder={isBuyingActive ? 'Auto-filled from offer documents' : 'Enter street'}
                    />
                    {formErrors.propertyAddress && (
                      <span className="purchase-field-error">{formErrors.propertyAddress}</span>
                    )}
                  </div>

                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">State (Optional)</label>
                    <input
                      type="text"
                      className={`purchase-form-input purchase-state-readonly ${formErrors.propertyState ? 'purchase-input-error' : ''}`}
                      value={getStateNameFromCode(formData.propertyState)}
                      readOnly
                      placeholder="State will be auto-populated"
                    />
                    {formErrors.propertyState && (
                      <span className="purchase-field-error">{formErrors.propertyState}</span>
                    )}
                  </div>

                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">City (Optional)</label>
                    <input
                      type="text"
                      className="purchase-form-input"
                      name="propertyCity"
                      value={formData.propertyCity}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                    />
                    {formErrors.propertyCity && (
                      <span className="purchase-field-error">{formErrors.propertyCity}</span>
                    )}
                  </div>

                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">Zip Code (Optional)</label>
                    <input
                      type="text"
                      className={`purchase-form-input ${zipCodeError ? 'purchase-input-error' : ''}`}
                      name="propertyZip"
                      value={formData.propertyZip}
                      onChange={handleInputChange}
                      placeholder={formData.propertyState ? `Enter zip code (e.g., ${ZIP_CODE_RANGES[getStateNameFromCode(formData.propertyState)]?.prefix.split(',')[0].split('-')[0] || ''}...)` : 'Enter zip code'}
                      maxLength="10"
                    />
                    {formData.propertyState && !zipCodeError && (
                      <span className="purchase-zip-hint">{getZipCodeHint(getStateNameFromCode(formData.propertyState))}</span>
                    )}
                    {zipCodeError && <span className="purchase-zip-error">{zipCodeError}</span>}
                  </div>

                  <div className="purchase-address-info-box">
                    <span className="purchase-address-info-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4B4B55" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    </span>
                    <span><strong>Info:</strong> Property address is optional — you can add or update these details later.</span>
                  </div>
                </div>

                {/* Financial Details */}
                <div className="purchase-form-row">
                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">Purchase Price (Optional)</label>
                    <input
                      type="number"
                      className="purchase-form-input"
                      name="purchasePrice"
                      value={formData.purchasePrice}
                      onChange={handleInputChange}
                      placeholder="950000"
                      min="0"
                    />
                  </div>
                  <div className="purchase-form-group">
                    <label className="purchase-form-label purchase-optional">Financing Type (Optional)</label>
                    <select
                      className="purchase-form-input purchase-form-select"
                      name="financingType"
                      value={formData.financingType}
                      onChange={handleInputChange}
                    >
                      <option value="">Select financing...</option>
                      {FINANCING_TYPES.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="purchase-form-group">
                  <label className="purchase-form-label purchase-optional">Estimated Close Date (Optional)</label>
                  <input
                    type="date"
                    className="purchase-form-input"
                    name="expectedClosingDate"
                    value={formData.expectedClosingDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="purchase-action-buttons">
                <button
                  className="purchase-btn purchase-btn-secondary"
                  onClick={() => navigate('/dashboard')}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="purchase-btn purchase-btn-primary"
                  onClick={createTransaction}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Transaction'}
                </button>
              </div>
            </div>
            )} {/* end !isBuyingActive */}
          </div>
        )}

        {/* SUCCESS SCREEN */}
        {showSuccess && (
          <div className="purchase-success-screen">
            <div className="purchase-success-icon">✓</div>
            <h1 className="purchase-success-title">Transaction Created!</h1>
            <p className="purchase-success-subtitle">Your purchase transaction has been created successfully</p>

            <div className="purchase-transaction-summary">
              <div className="purchase-summary-section">
                <div className="purchase-summary-section-title">Transaction Information</div>
                <div className="purchase-summary-grid">
                  <div className="purchase-summary-item">
                    <span className="purchase-summary-label">Transaction ID</span>
                    <span className="purchase-summary-value">{transactionSummary.transactionId}</span>
                  </div>
                  <div className="purchase-summary-item">
                    <span className="purchase-summary-label">Status</span>
                    <span className="purchase-summary-value purchase-status-badge">{transactionSummary.statusLabel}</span>
                  </div>
                </div>
              </div>

              <div className="purchase-summary-section">
                <div className="purchase-summary-section-title">Property Address</div>
                <div className="purchase-summary-full-address">{transactionSummary.fullAddress}</div>
              </div>
            </div>

            <div className="purchase-next-steps">
              <h3 className="purchase-next-steps-title">Recommended Next Steps</h3>
              <div className="purchase-action-card">
                <div className="purchase-action-icon">📧</div>
                <div className="purchase-action-content">
                  <div className="purchase-action-title">Invite Buyer</div>
                  <div className="purchase-action-description">Send invitation email to buyer(s) to join transaction</div>
                </div>
                <div className="purchase-action-arrow">→</div>
              </div>
              <div className="purchase-action-card">
                <div className="purchase-action-icon">📅</div>
                <div className="purchase-action-content">
                  <div className="purchase-action-title">Add Calendar Entry</div>
                  <div className="purchase-action-description">Schedule property viewing or important dates</div>
                </div>
                <div className="purchase-action-arrow">→</div>
              </div>
              <div className="purchase-action-card">
                <div className="purchase-action-icon">📄</div>
                <div className="purchase-action-content">
                  <div className="purchase-action-title">Upload Documents</div>
                  <div className="purchase-action-description">Add purchase agreement, pre-approval, or disclosures</div>
                </div>
                <div className="purchase-action-arrow">→</div>
              </div>
            </div>

            <button className="purchase-view-transaction-btn" onClick={handleViewTransaction}>
              View Transaction Details
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseTransactionPage;
